import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import RequestReset from './RequestReset';
import { detectTimeZone, getTimeZoneOptions } from '../utils/timezones';

const formatDateTime = (value) => {
  if (!value) return 'Never';

  const date = new Date(Number(value));

  if (Number.isNaN(date.getTime())) return 'Unknown';

  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

export default function Settings({ user, theme, onToggleTheme, onUserUpdate }) {
  const username = user?.data?.username || '';
  const email = user?.data?.email || '';
  const isAdmin = Boolean(user?.data?.is_admin ?? user?.data?.isAdmin);
  const initialTimezone = user?.data?.timezone || detectTimeZone();
  const [timezone, setTimezone] = useState(initialTimezone);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [staleDays, setStaleDays] = useState(90);
  const [staleUsers, setStaleUsers] = useState([]);
  const [loadingStale, setLoadingStale] = useState(false);
  const [adminStatus, setAdminStatus] = useState('');
  const [adminError, setAdminError] = useState('');

  useEffect(() => {
    setTimezone(initialTimezone);
  }, [initialTimezone]);

  const timeZoneOptions = useMemo(() => getTimeZoneOptions(), []);

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '44px',
      backgroundColor: 'var(--surface)',
      borderColor: state.isFocused ? 'var(--brand)' : 'var(--border)',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(102, 126, 234, 0.18)' : 'none',
      borderRadius: '12px',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'var(--panel-text)',
    }),
    input: (provided) => ({
      ...provided,
      color: 'var(--panel-text)',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'var(--panel-muted)',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'var(--surface-strong)',
      borderRadius: '12px',
      overflow: 'hidden',
      zIndex: 20,
    }),
    menuList: (provided) => ({
      ...provided,
      backgroundColor: 'var(--surface-strong)',
      maxHeight: '240px',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? 'rgba(102, 126, 234, 0.12)' : 'transparent',
      color: 'var(--panel-text)',
    }),
  };

  const saveTimezone = async () => {
    if (!username || !timezone) return;
    setSaving(true);
    setStatus('');

    try {
      const res = await axios.put('/api/usersettings', {
        username,
        timezone,
      });

      const updatedUser = {
        ...user,
        data: {
          ...user.data,
          timezone: res.data.user.timezone,
        },
      };

      onUserUpdate?.(updatedUser);
      setStatus('Timezone updated.');
    } catch (err) {
      setStatus(err?.response?.data?.message || 'Failed to save timezone.');
    } finally {
      setSaving(false);
    }
  };

  const loadStaleUsers = async (daysOverride = staleDays) => {
    if (!isAdmin || !username) return;

    setLoadingStale(true);
    setAdminError('');
    setAdminStatus('');

    try {
      const res = await axios.get('/api/admin', {
        params: {
          action: 'stale-users',
          adminUsername: username,
          days: daysOverride,
        },
      });

      setStaleUsers(res.data.users || []);
      setAdminStatus(
        res.data.users?.length
          ? `Found ${res.data.users.length} stale account${res.data.users.length === 1 ? '' : 's'}.`
          : 'No stale accounts found.'
      );
    } catch (err) {
      setAdminError(err?.response?.data?.message || 'Failed to load stale accounts.');
    } finally {
      setLoadingStale(false);
    }
  };

  useEffect(() => {
    if (isAdmin && username) {
      loadStaleUsers(staleDays);
    } else {
      setStaleUsers([]);
      setAdminStatus('');
      setAdminError('');
    }
    // load once when admin status/user changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, username]);

  const deleteUser = async (targetUsername) => {
    if (!window.confirm(`Delete ${targetUsername} and all of their data?`)) return;

    setLoadingStale(true);
    setAdminError('');

    try {
      await axios.post('/api/admin', {
        action: 'delete-user',
        adminUsername: username,
        targetUsername,
      });

      setAdminStatus(`Deleted ${targetUsername}.`);
      await loadStaleUsers(staleDays);
    } catch (err) {
      setAdminError(err?.response?.data?.message || `Failed to delete ${targetUsername}.`);
    } finally {
      setLoadingStale(false);
    }
  };

  const deleteStaleUsers = async () => {
    if (!staleUsers.length) return;

    if (
      !window.confirm(
        `Delete all ${staleUsers.length} stale account${staleUsers.length === 1 ? '' : 's'} older than ${staleDays} days?`
      )
    )
      return;

    setLoadingStale(true);
    setAdminError('');

    try {
      const res = await axios.post('/api/admin', {
        action: 'delete-stale-users',
        adminUsername: username,
        days: staleDays,
      });

      setAdminStatus(
        `Deleted ${res.data.deletedCount || 0} stale account${(res.data.deletedCount || 0) === 1 ? '' : 's'}.`
      );
      await loadStaleUsers(staleDays);
    } catch (err) {
      setAdminError(err?.response?.data?.message || 'Failed to delete stale accounts.');
    } finally {
      setLoadingStale(false);
    }
  };

  const isDark = theme === 'dark';

  return (
    <section className="settings-page">
      <div className="settings-card">
        <div>
          <h1 className="settings-card__eyebrow">Account settings</h1>
          <p className="settings-card__copy">
            Set the timezone used for scheduled email digests and switch the app theme here.
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            width: '100%',
            height: '100%',
            gap: '2rem',
            alignItems: 'stretch',
            flex: 1,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: 1 }}>
            <div className="settings-card__meta">
              <div>
                <span className="settings-card__label">Username</span>
                <strong>{username || 'Unknown'}</strong>
              </div>
              <div>
                <span className="settings-card__label">Email</span>
                <strong>{email || 'Not available'}</strong>
              </div>
            </div>

            <div className="settings-group" style={{ flex: 1 }}>
              <div className="settings-group__header">
                <div>
                  <p className="settings-group__label">Timezone</p>
                  <p className="settings-group__help">
                    Used to decide when your twice-daily digest is sent.
                  </p>
                </div>
              </div>

              <Select
                value={timeZoneOptions.find((option) => option.value === timezone) || null}
                onChange={(option) => setTimezone(option?.value || detectTimeZone())}
                options={timeZoneOptions}
                styles={selectStyles}
                placeholder="Select your timezone"
                isSearchable={true}
              />

              <button
                type="button"
                className="settings-save"
                onClick={saveTimezone}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save timezone'}
              </button>

              {status && <p className="settings-status">{status}</p>}
            </div>

            <div className="settings-group" style={{ flex: 1 }}>
              <div className="settings-group__header">
                <div>
                  <p className="settings-group__label">Theme</p>
                  <p className="settings-group__help">
                    Move the slider to switch between light and dark mode.
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="theme-slider"
                onClick={onToggleTheme}
                aria-pressed={isDark}
                style={{ marginTop: '1.75rem' }}
                aria-label="Toggle theme"
              >
                <span className="theme-slider__label">Light</span>
                <span className={`theme-slider__track ${isDark ? 'is-dark' : 'is-light'}`}>
                  <span className={`theme-slider__thumb ${isDark ? 'is-dark' : 'is-light'}`} />
                </span>
                <span className="theme-slider__label">Dark</span>
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: 1 }}>
            {isAdmin && (
              <div className="settings-group admin-tools">
                <div className="settings-group__header">
                  <div>
                    <p className="settings-group__label">Admin tools</p>
                    <p className="settings-group__help">
                      Review and remove stale accounts before they accumulate in production.
                    </p>
                  </div>
                </div>

                <div className="admin-tools__controls">
                  <label className="admin-tools__field">
                    <span className="admin-tools__field-label">Stale after (days)</span>
                    <input
                      type="number"
                      min="1"
                      max="3650"
                      value={staleDays}
                      onChange={(event) => setStaleDays(Number(event.target.value) || 1)}
                      className="admin-tools__input"
                    />
                  </label>

                  <button
                    type="button"
                    className="settings-save"
                    onClick={() => loadStaleUsers(staleDays)}
                    disabled={loadingStale}
                  >
                    {loadingStale ? 'Loading...' : 'Load stale accounts'}
                  </button>

                  {staleUsers.length > 0 && (
                    <button
                      type="button"
                      className="settings-save admin-tools__danger"
                      onClick={deleteStaleUsers}
                      disabled={loadingStale || staleUsers.length === 0}
                    >
                      Delete all listed
                    </button>
                  )}
                </div>

                {adminStatus && <p className="settings-status">{adminStatus}</p>}
                {adminError && <p className="settings-status admin-tools__error">{adminError}</p>}

                <div className="admin-tools__list" style={{ overflowY: 'auto', height: '8.5rem' }}>
                  {staleUsers.length === 0 && !loadingStale ? (
                    <p className="admin-tools__empty" style={{ marginTop: 'auto' }}>
                      No stale accounts to show for this threshold.
                    </p>
                  ) : (
                    staleUsers.map((staleUser) => (
                      <div key={staleUser.id} className="admin-tools__row">
                        <div className="admin-tools__row-main">
                          <strong className="admin-tools__row-title">{staleUser.username}</strong>
                          <span className="admin-tools__row-subtext">
                            {staleUser.email || 'No email'} · Last login{' '}
                            {formatDateTime(staleUser.last_login_at)} · Created{' '}
                            {formatDateTime(staleUser.created_at)}
                          </span>
                        </div>

                        <div className="admin-tools__row-actions">
                          <span className="admin-tools__row-meta">
                            {staleUser.ageDays != null
                              ? `${staleUser.ageDays} days old`
                              : 'Age unknown'}
                          </span>
                          <button
                            type="button"
                            className="settings-save admin-tools__danger"
                            onClick={() => deleteUser(staleUser.username)}
                            disabled={loadingStale}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
            <div className="settings-group" style={{ flex: 1 }}>
              <RequestReset initialUsername={username} />
            </div>
          </div>
        </div>
        <div className="settings-card__footer">
          <Link to="/todo" className="settings-card__backlink">
            Back to To-Do
          </Link>
        </div>
      </div>
    </section>
  );
}
